/**
 * Shared constants, variables, emoji data, and helpers for the SMS editor.
 * Single source of truth used by SmsEditor, SmsEmojiPicker, SmsCampaignEditorPage,
 * and SmsTemplateBuilderPage.
 *
 * Variables match the PHP SmsVariables::getAvailableVariables() in
 * Follow-That-Patient/html/sms-system/classes/SmsVariables.php
 *
 * Emoji categories match Follow-That-Patient/html/sms-system/includes/emoji-picker.php
 */

// ─── SMS Merge Variables ────────────────────────────────────────────────────

export const SMS_VARIABLES = [
  { key: '{{first_name}}', desc: 'Recipient first name' },
  { key: '{{last_name}}', desc: 'Recipient last name' },
  { key: '{{facility_id}}', desc: 'Facility ID' },
  { key: '{{facility_name}}', desc: 'Facility name' },
  { key: '{{facility_address}}', desc: 'Facility address' },
  { key: '{{facility_city}}', desc: 'Facility city' },
  { key: '{{facility_state}}', desc: 'Facility state' },
  { key: '{{facility_zip}}', desc: 'Facility zip / postal code' },
  { key: '{{facility_country}}', desc: 'Facility country' },
  { key: '{{facility_phone}}', desc: 'Facility phone number' },
  { key: '{{facility_email}}', desc: 'Facility email address' },
  { key: '{{facility_website}}', desc: 'Facility website URL' },
  { key: '{{facility_facebook}}', desc: 'Facility Facebook URL' },
  { key: '{{facility_twitter}}', desc: 'Facility Twitter / X URL' },
  { key: '{{facility_instagram}}', desc: 'Facility Instagram URL' },
  { key: '{{facility_pinterest}}', desc: 'Facility Pinterest URL' },
  { key: '{{facility_linkedin}}', desc: 'Facility LinkedIn URL' },
  { key: '{{facility_tiktok}}', desc: 'Facility TikTok URL' },
  { key: '{{facility_youtube}}', desc: 'Facility YouTube URL' },
  { key: '{{opt_out}}', desc: 'Opt-out instructions' },
] as const;

// ─── Opt-out instructions ───────────────────────────────────────────────────

/**
 * The opt-out token. Every send resolves it to the account's "SMS opt-out
 * text" (Marketing Settings → SMS Settings; "Reply STOP to opt out." when
 * unset), so it is the SMS twin of the email `{{unsubscribe_link}}`.
 *
 * What counts as opt-out instructions is decided here and, identically, in
 * MarketingVariableService::hasSmsOptOut() on the API. Change both together.
 */
export const SMS_OPT_OUT_TOKEN = '{{opt_out}}';

/** `{{opt_out}}`, tolerating whitespace and an inline `[[default]]` the author added. */
const OPT_OUT_TOKEN_SOURCE = '\\{\\{\\s*opt_out(?:\\s*\\[\\[[\\s\\S]*?\\]\\])?\\s*\\}\\}';
const OPT_OUT_TOKEN_PATTERN = new RegExp(OPT_OUT_TOKEN_SOURCE, 'i');
const OPT_OUT_TOKEN_PATTERN_ALL = new RegExp(OPT_OUT_TOKEN_SOURCE, 'gi');

/**
 * The carrier keyword written out by the author ("Text STOP to end"). Upper
 * case only: "stop by the office" is not an opt-out.
 */
const STOP_KEYWORD_PATTERN = /\bSTOP\b/;

/** True when the message carries opt-out instructions: the token, or a literal STOP. */
export function hasSmsOptOut(body: string | null | undefined): boolean {
  if (!body) return false;
  return OPT_OUT_TOKEN_PATTERN.test(body) || STOP_KEYWORD_PATTERN.test(body);
}

/** The message with every opt-out token taken out — what the recipient reads besides the instructions. */
export function smsBodyWithoutOptOut(body: string | null | undefined): string {
  return (body ?? '').replace(OPT_OUT_TOKEN_PATTERN_ALL, '');
}

/**
 * True when there is a message beyond the opt-out instructions. A body that is
 * only `{{opt_out}}` has nothing to opt out of, so it is "no content" for the
 * send, activation and "add message content" checks alike.
 */
export function hasSmsMessageContent(body: string | null | undefined): boolean {
  return smsBodyWithoutOptOut(body).trim().length > 0;
}

/**
 * The body with opt-out instructions guaranteed: the token is appended when
 * missing, on its own line after a blank one so it never runs into the last
 * sentence. An empty message is returned as-is (see hasSmsMessageContent).
 *
 * Every save and every template apply goes through this, and the send
 * pipeline applies the same rule as a backstop
 * (MarketingVariableService::ensureSmsOptOut).
 */
export function ensureSmsOptOut(body: string): string {
  if (hasSmsOptOut(body)) return body;
  const trimmed = body.replace(/\s+$/, '');
  if (trimmed === '') return body;
  return `${trimmed}\n\n${SMS_OPT_OUT_TOKEN}`;
}

// ─── Cost / Limit Constants ─────────────────────────────────────────────────

export const SMS_COST_PER_SEGMENT = 0.0083;
export const MMS_COST_PER_MSG = 0.022;
export const MMS_MAX_FILES = 10;
export const SMS_MAX_CHARS = 1600;
export const MMS_MAX_BYTES = 5 * 1024 * 1024;

// ─── MMS Attachment Formats ─────────────────────────────────────────────────

/**
 * The only image formats a picture message can carry, mirroring
 * MmsMediaService::SENDABLE_MIME_TYPES on the API.
 *
 * Twilio fetches each attached URL and rejects the WHOLE message (error 12300)
 * for anything else. A WebP is the usual culprit: it is a perfectly good format
 * in the app and on the website, which is why the Photo Library accepts it, and
 * it is the one that took down an entire SMS campaign.
 *
 * Uploads through the Photo Library are converted to JPEG on the way in, so new
 * images are safe by construction. What this guards is the OTHER way an image
 * gets attached — picking one that was already in the library from before that
 * conversion existed. Those are still WebP, and nothing about browsing to one
 * would tell you that.
 */
export const MMS_SENDABLE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif'];

/**
 * Whether this library URL can go on a picture message.
 *
 * Read from the extension rather than a content type because the photo list
 * endpoint does not return one — and it is reliable here, since every key in the
 * bucket is generated by our own upload, which names the file after the format
 * it actually wrote.
 *
 * A URL with no extension at all is allowed through: we cannot prove it is
 * wrong, and the API still checks the real content type before the send. This
 * gate exists to catch what we CAN see, not to be the only line of defence.
 */
export function isMmsSendableUrl(url: string): boolean {
  const ext = mediaExtension(url);

  return ext === '' || MMS_SENDABLE_EXTENSIONS.includes(ext);
}

/** What to tell someone who picked an image a picture message cannot carry. */
export function mmsMediaRejectionReason(url: string): string {
  const ext = mediaExtension(url);
  const label = ext === 'webp' ? 'WebP' : ext.toUpperCase();

  // Points at the Upload tab because that route DOES fix it: the same file
  // uploaded there is re-encoded to JPEG on the way in. Only this one — picking
  // an image already in the library — attaches the original bytes untouched.
  return `${label} images cannot be sent in a picture message — carriers only `
    + 'display JPEG, PNG and GIF. Upload this image from the Upload tab and it '
    + 'will be converted, or pick a different one.';
}

/** Lowercase extension of a URL, ignoring any query string. */
function mediaExtension(url: string): string {
  const path = (url || '').split(/[?#]/)[0];
  const match = path.match(/\.([a-z0-9]+)$/i);

  return match ? match[1].toLowerCase() : '';
}

// ─── Request-info links ─────────────────────────────────────────────────────

/**
 * The most characters personalization adds to ONE generated request-info link.
 *
 * Authors paste the bare link (`https://<host>/l/<code>`), but every recipient is
 * sent their own copy with a signed suffix appended (`…/l/<code>/<suffix>`). The
 * segment count an author sees has to be the one the recipient pays for, so each
 * pasted link is counted at its personalized length. 17 is the automation-step
 * worst case; a campaign link adds up to 13, so a campaign may count slightly
 * high, never low. Mirrors `personalizedExtraChars` from the links API.
 */
export const REQUEST_INFO_PERSONALIZED_EXTRA_CHARS = 17;

/**
 * A BARE generated request-info link, on any host: `/l/` plus exactly seven
 * lowercase base36 characters, an optional single `/`, then the end of the text
 * or a character that cannot continue the path (space, `.`, `?`, `"`, …).
 *
 * Deliberately does not match:
 *  - a link that already carries a suffix (`/l/abc1234/s2k9x1h7q2m`) — it is
 *    already at full length;
 *  - a longer path that merely starts with a code (`/l/abc12345`).
 *
 * Not global, so `.test()` is safe to call on it. Count occurrences with
 * countRequestInfoLinks(), which builds its own global copy.
 */
export const REQUEST_INFO_LINK_PATTERN = /\/l\/[a-z0-9]{7}\/?(?![a-z0-9/])/;

/** How many bare request-info links the text carries. */
export function countRequestInfoLinks(text: string): number {
  if (!text) return 0;
  return text.match(new RegExp(REQUEST_INFO_LINK_PATTERN.source, 'g'))?.length ?? 0;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Segments this body will cost per recipient.
 *
 * Each bare request-info link is padded to its personalized length before
 * counting (see REQUEST_INFO_PERSONALIZED_EXTRA_CHARS), so every caller —
 * the editors, the template cards and calcCost — shows the cost the recipient
 * is actually sent. The padding is ASCII and cannot flip a body into the
 * unicode (70-character) tier on its own.
 */
export function getSmsSegments(text: string): number {
  if (!text || text.length === 0) return 0;
  const links = countRequestInfoLinks(text);
  const counted = links > 0
    ? text + 'x'.repeat(links * REQUEST_INFO_PERSONALIZED_EXTRA_CHARS)
    : text;
  const hasUnicode = /[^\x00-\x7F]/.test(counted);
  return Math.ceil(counted.length / (hasUnicode ? 70 : 160));
}

export function segmentColorClass(segments: number): string {
  if (segments === 0) return 'bg-gray-400';
  if (segments <= 4) return 'bg-green-500';
  if (segments <= 8) return 'bg-sky-500';
  if (segments <= 10) return 'bg-orange-500';
  return 'bg-red-500';
}

export function calcCost(
  contactCount: number,
  messageBody: string,
  hasMedia: boolean,
): { total: number; label: string } {
  if (hasMedia) {
    return {
      total: contactCount * MMS_COST_PER_MSG,
      label: `MMS @ $${MMS_COST_PER_MSG}/msg \u00b7 Twilio pricing`,
    };
  }
  const segs = getSmsSegments(messageBody);
  return {
    total: contactCount * segs * SMS_COST_PER_SEGMENT,
    label: `${segs === 1 ? '1 segment' : `${segs} segments`} @ $${SMS_COST_PER_SEGMENT}/seg \u00b7 Twilio pricing`,
  };
}

// ─── Emoji Categories (matches PHP emoji-picker.php) ────────────────────────
// Each item is [emoji, keywords] to support keyword search.

export interface EmojiItem {
  e: string;
  k: string;
}

export interface EmojiCategory {
  id: string;
  icon: string;
  label: string;
  items: [string, string][];
}

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'smileys', icon: '\u{1F60A}', label: 'Smileys & Faces',
    items: [
      ['\u{1F600}','grinning face happy'],
      ['\u{1F603}','smiley face happy mouth'],
      ['\u{1F604}','smile face happy eyes'],
      ['\u{1F601}','beaming grin teeth'],
      ['\u{1F606}','laughing squint happy'],
      ['\u{1F605}','sweat smile grin'],
      ['\u{1F923}','rofl rolling floor laugh'],
      ['\u{1F602}','joy tears laugh cry'],
      ['\u{1F642}','slight smile'],
      ['\u{1F643}','upside down face silly'],
      ['\u{1F609}','wink face flirt'],
      ['\u{1F60A}','blush smile happy warm'],
      ['\u{1F607}','angel halo innocent'],
      ['\u{1F970}','smiling hearts love adore'],
      ['\u{1F60D}','heart eyes love crush'],
      ['\u{1F929}','star struck amazed wow'],
      ['\u{1F618}','kiss blowing love'],
      ['\u{1F617}','kissing face'],
      ['\u{1F61A}','kissing closed eyes'],
      ['\u{1F619}','kissing smile'],
      ['\u{1F60B}','yummy delicious tongue food'],
      ['\u{1F61B}','tongue out playful'],
      ['\u{1F61C}','wink tongue playful crazy'],
      ['\u{1F92A}','zany crazy wild goofy'],
      ['\u{1F61D}','tongue squint playful'],
      ['\u{1F911}','money mouth rich dollar'],
      ['\u{1F917}','hugging hug warm'],
      ['\u{1F92D}','hand over mouth giggle shy'],
      ['\u{1F92B}','shushing quiet secret hush'],
      ['\u{1F914}','thinking hmm wonder'],
      ['\u{1F910}','zipper mouth shut quiet'],
      ['\u{1F928}','raised eyebrow skeptical'],
      ['\u{1F610}','neutral face meh'],
      ['\u{1F611}','expressionless blank'],
      ['\u{1F636}','no mouth silent'],
      ['\u{1F60F}','smirk sly smug'],
      ['\u{1F612}','unamused annoyed'],
      ['\u{1F644}','eye roll annoyed whatever'],
      ['\u{1F62C}','grimace teeth awkward'],
      ['\u{1F925}','lying pinocchio nose'],
      ['\u{1F60C}','relieved peaceful calm'],
      ['\u{1F614}','pensive sad thoughtful'],
      ['\u{1F971}','yawn tired sleepy bored'],
      ['\u{1F62A}','sleepy tired drool'],
      ['\u{1F924}','drooling drool'],
      ['\u{1F634}','sleeping zzz asleep'],
      ['\u{1F637}','mask sick medical face'],
      ['\u{1F912}','thermometer sick fever ill'],
      ['\u{1F915}','bandage hurt injured'],
      ['\u{1F922}','nauseous sick green'],
      ['\u{1F92E}','vomit sick throw up'],
      ['\u{1F975}','hot warm overheated sweat'],
      ['\u{1F976}','cold freezing ice'],
      ['\u{1F974}','woozy dizzy drunk'],
      ['\u{1F635}','dizzy dazed knocked out'],
      ['\u{1F92F}','exploding head mind blown shocked'],
      ['\u{1F920}','cowboy hat western yeehaw'],
      ['\u{1F973}','party celebrate horn hat'],
      ['\u{1F60E}','sunglasses cool awesome'],
      ['\u{1F913}','nerd glasses geek smart'],
      ['\u{1F9D0}','monocle classy inspect'],
      ['\u{1F615}','confused puzzled'],
      ['\u{1F61F}','worried nervous anxious'],
      ['\u{1F641}','frown sad slightly'],
      ['\u{1F62E}','open mouth surprised oh'],
      ['\u{1F62F}','hushed surprised quiet'],
      ['\u{1F632}','astonished shocked wow'],
      ['\u{1F633}','flushed embarrassed red'],
      ['\u{1F97A}','pleading puppy eyes cute'],
      ['\u{1F626}','frowning open mouth'],
      ['\u{1F627}','anguished face'],
      ['\u{1F628}','fearful scared afraid'],
      ['\u{1F630}','anxious sweat worry'],
      ['\u{1F625}','sad relieved sweat'],
      ['\u{1F622}','crying tear sad'],
      ['\u{1F62D}','sobbing loud cry tears'],
      ['\u{1F631}','screaming fear horror'],
      ['\u{1F616}','confounded face frustrated'],
      ['\u{1F623}','persevering determined'],
      ['\u{1F61E}','disappointed sad'],
      ['\u{1F613}','downcast sweat sad'],
      ['\u{1F629}','weary tired exhausted'],
      ['\u{1F62B}','tired face exhausted'],
      ['\u{1F624}','steam nose triumph angry'],
      ['\u{1F621}','angry red mad'],
      ['\u{1F620}','angry face mad'],
      ['\u{1F92C}','cursing swearing angry symbols'],
      ['\u{1F608}','devil smiling horns purple'],
      ['\u{1F47F}','devil angry imp'],
      ['\u{1F480}','skull death dead'],
      ['\u{2620}\u{FE0F}','skull crossbones danger poison'],
      ['\u{1F4A9}','poop poo'],
      ['\u{1F921}','clown face joker'],
      ['\u{1F47B}','ghost boo spooky halloween'],
      ['\u{1F47D}','alien space extraterrestrial'],
      ['\u{1F47E}','alien monster space invader'],
      ['\u{1F916}','robot face bot'],
    ],
  },
  {
    id: 'hands', icon: '\u{1F44B}', label: 'Hands & People',
    items: [
      ['\u{1F44B}','wave hello goodbye hand hi bye'],
      ['\u{1F91A}','raised back hand stop'],
      ['\u{1F590}\u{FE0F}','hand fingers splayed open'],
      ['\u{270B}','raised hand stop high five'],
      ['\u{1F596}','vulcan spock live long'],
      ['\u{1F44C}','ok okay perfect fine'],
      ['\u{1F90F}','pinching small tiny bit'],
      ['\u{270C}\u{FE0F}','victory peace sign two'],
      ['\u{1F91E}','crossed fingers luck hope'],
      ['\u{1F91F}','love you gesture rock'],
      ['\u{1F918}','rock on horns metal'],
      ['\u{1F919}','call me phone hand hang loose'],
      ['\u{1F448}','pointing left'],
      ['\u{1F449}','pointing right'],
      ['\u{1F446}','pointing up'],
      ['\u{1F595}','middle finger'],
      ['\u{1F447}','pointing down'],
      ['\u{261D}\u{FE0F}','index pointing up one'],
      ['\u{1F44D}','thumbs up like good yes approve'],
      ['\u{1F44E}','thumbs down dislike bad no'],
      ['\u{270A}','raised fist solidarity strong'],
      ['\u{1F44A}','fist bump punch'],
      ['\u{1F91B}','left fist bump'],
      ['\u{1F91C}','right fist bump'],
      ['\u{1F44F}','clap applause bravo hands'],
      ['\u{1F64C}','raised hands celebration hooray'],
      ['\u{1F450}','open hands jazz'],
      ['\u{1F932}','palms up together prayer'],
      ['\u{1F91D}','handshake deal agreement'],
      ['\u{1F64F}','pray please thank you folded hands'],
      ['\u{270D}\u{FE0F}','writing hand pen'],
      ['\u{1F485}','nail polish manicure'],
      ['\u{1F933}','selfie phone camera'],
      ['\u{1F4AA}','bicep strong muscle flex arm'],
      ['\u{1F9BE}','mechanical arm prosthetic'],
      ['\u{1F9BF}','mechanical leg prosthetic'],
      ['\u{1F9B5}','leg kick foot'],
      ['\u{1F9B6}','foot bare toe'],
      ['\u{1F442}','ear listen hear'],
      ['\u{1F443}','nose smell sniff'],
      ['\u{1F9E0}','brain smart think mind'],
      ['\u{1F440}','eyes look see watch'],
      ['\u{1F441}\u{FE0F}','eye look see single'],
      ['\u{1F445}','tongue taste lick'],
      ['\u{1F444}','mouth lips kiss'],
      ['\u{1F476}','baby infant child'],
      ['\u{1F9D2}','child kid young'],
      ['\u{1F466}','boy male young'],
      ['\u{1F467}','girl female young'],
      ['\u{1F9D1}','person adult'],
      ['\u{1F468}','man male adult guy'],
      ['\u{1F469}','woman female adult lady'],
      ['\u{1F9D3}','older person elder senior'],
      ['\u{1F474}','old man grandfather grandpa'],
      ['\u{1F475}','old woman grandmother grandma'],
    ],
  },
  {
    id: 'hearts', icon: '\u{2764}\u{FE0F}', label: 'Hearts & Love',
    items: [
      ['\u{2764}\u{FE0F}','red heart love'],
      ['\u{1F9E1}','orange heart'],
      ['\u{1F49B}','yellow heart'],
      ['\u{1F49A}','green heart'],
      ['\u{1F499}','blue heart'],
      ['\u{1F49C}','purple heart'],
      ['\u{1F5A4}','black heart'],
      ['\u{1F90D}','white heart'],
      ['\u{1F90E}','brown heart'],
      ['\u{1F494}','broken heart'],
      ['\u{2763}\u{FE0F}','heart exclamation'],
      ['\u{1F495}','two hearts love'],
      ['\u{1F49E}','revolving hearts'],
      ['\u{1F493}','beating heart pulse'],
      ['\u{1F497}','growing heart'],
      ['\u{1F496}','sparkling heart'],
      ['\u{1F498}','cupid arrow heart love'],
      ['\u{1F49D}','heart ribbon gift'],
      ['\u{1F49F}','heart decoration'],
      ['\u{2665}\u{FE0F}','heart suit card'],
      ['\u{1F48B}','kiss lips mark lipstick'],
      ['\u{1F48C}','love letter envelope heart'],
      ['\u{1F490}','bouquet flowers'],
      ['\u{1F339}','rose red flower romantic'],
      ['\u{1F940}','wilted flower dead rose'],
      ['\u{1F48D}','ring engagement diamond wedding'],
      ['\u{1F48E}','gem diamond jewel precious'],
    ],
  },
  {
    id: 'celebrate', icon: '\u{1F389}', label: 'Celebration & Activities',
    items: [
      ['\u{1F389}','party popper celebrate tada'],
      ['\u{1F38A}','confetti ball celebrate'],
      ['\u{1F388}','balloon party birthday'],
      ['\u{1F382}','birthday cake candles'],
      ['\u{1F381}','gift present wrapped birthday'],
      ['\u{1F380}','ribbon bow decoration'],
      ['\u{1F384}','christmas tree holiday xmas'],
      ['\u{1F383}','jack o lantern pumpkin halloween'],
      ['\u{1F397}\u{FE0F}','reminder ribbon awareness'],
      ['\u{1F39F}\u{FE0F}','admission ticket event'],
      ['\u{1F3AB}','ticket event admission'],
      ['\u{1F396}\u{FE0F}','military medal honor'],
      ['\u{1F3C6}','trophy winner champion cup gold'],
      ['\u{1F3C5}','sports medal award'],
      ['\u{1F947}','first place gold medal winner'],
      ['\u{1F948}','second place silver medal'],
      ['\u{1F949}','third place bronze medal'],
      ['\u{26BD}','soccer football ball sport'],
      ['\u{1F3C0}','basketball ball sport'],
      ['\u{1F3C8}','american football ball sport'],
      ['\u{26BE}','baseball ball sport'],
      ['\u{1F3BE}','tennis ball sport racket'],
      ['\u{1F3D0}','volleyball ball sport'],
      ['\u{1F3C9}','rugby ball sport'],
      ['\u{1F3B1}','billiards pool 8ball'],
      ['\u{1F3D3}','ping pong table tennis'],
      ['\u{1F3F8}','badminton shuttlecock sport'],
      ['\u{26F3}','golf flag hole sport'],
      ['\u{1F3AF}','bullseye target dart direct hit'],
      ['\u{1F3A3}','fishing rod fish hook'],
      ['\u{1F94A}','boxing glove fight sport'],
      ['\u{1F94B}','martial arts karate uniform'],
      ['\u{1F3BD}','running shirt sport'],
      ['\u{1F6F9}','skateboard'],
      ['\u{26F8}','ice skate skating winter'],
      ['\u{1F3BF}','ski skiing snow winter'],
      ['\u{1F3C2}','snowboarder snow winter'],
      ['\u{1F3CB}\u{FE0F}','weightlifter gym exercise'],
      ['\u{1F938}','cartwheel gymnastics'],
      ['\u{1F3CA}\u{FE0F}','swimmer swimming pool'],
      ['\u{1F6A3}','rowing boat'],
      ['\u{1F9D7}','climbing rock'],
      ['\u{1F6B5}','mountain biking cycling'],
      ['\u{1F6B4}','biking cycling'],
      ['\u{1F3AE}','video game controller gaming'],
      ['\u{1F3B2}','dice game roll'],
      ['\u{1F9E9}','puzzle piece jigsaw'],
      ['\u{265F}\u{FE0F}','chess pawn strategy'],
      ['\u{1F3A8}','artist palette paint art'],
      ['\u{1F3AD}','performing arts theater masks drama'],
      ['\u{1F3B5}','musical note music song'],
      ['\u{1F3B6}','musical notes music song melody'],
      ['\u{1F3A4}','microphone sing karaoke music'],
      ['\u{1F3A7}','headphone music listen audio'],
      ['\u{1F3B9}','musical keyboard piano'],
      ['\u{1F3B7}','saxophone jazz music'],
      ['\u{1F3B8}','guitar music rock'],
      ['\u{1F941}','drum music beat'],
    ],
  },
  {
    id: 'nature', icon: '\u{1F31F}', label: 'Nature & Weather',
    items: [
      ['\u{2B50}','star gold yellow'],
      ['\u{1F31F}','glowing star sparkle bright'],
      ['\u{2728}','sparkles magic shine glitter'],
      ['\u{26A1}','lightning bolt electric zap thunder'],
      ['\u{1F525}','fire hot flame lit'],
      ['\u{1F4A5}','boom collision explosion'],
      ['\u{2604}\u{FE0F}','comet shooting star space'],
      ['\u{1F308}','rainbow colorful pride'],
      ['\u{2600}\u{FE0F}','sun sunny bright warm'],
      ['\u{1F324}\u{FE0F}','sun behind small cloud partly'],
      ['\u{26C5}','sun behind cloud partly cloudy'],
      ['\u{2601}\u{FE0F}','cloud cloudy overcast'],
      ['\u{1F326}\u{FE0F}','sun behind rain cloud'],
      ['\u{1F327}\u{FE0F}','rain cloud raining'],
      ['\u{26C8}\u{FE0F}','thunderstorm lightning rain'],
      ['\u{1F329}\u{FE0F}','lightning cloud bolt'],
      ['\u{1F32A}\u{FE0F}','tornado twister storm'],
      ['\u{1F32B}\u{FE0F}','fog foggy mist'],
      ['\u{1F32C}\u{FE0F}','wind blowing face breeze'],
      ['\u{1F300}','cyclone hurricane spiral dizzy'],
      ['\u{1F30A}','wave water ocean sea surf'],
      ['\u{1F4A7}','droplet water rain tear'],
      ['\u{1F4A6}','sweat splashing water'],
      ['\u{2614}','umbrella rain'],
      ['\u{2744}\u{FE0F}','snowflake snow cold winter'],
      ['\u{26C4}','snowman snow winter'],
      ['\u{1F321}\u{FE0F}','thermometer temperature'],
      ['\u{1F338}','cherry blossom flower spring pink'],
      ['\u{1F4AE}','white flower'],
      ['\u{1F3F5}\u{FE0F}','rosette flower'],
      ['\u{1F33A}','hibiscus flower tropical'],
      ['\u{1F33B}','sunflower flower yellow'],
      ['\u{1F33C}','blossom flower daisy'],
      ['\u{1F337}','tulip flower spring'],
      ['\u{1F331}','seedling plant sprout growing'],
      ['\u{1F332}','evergreen tree pine christmas'],
      ['\u{1F333}','deciduous tree oak'],
      ['\u{1F334}','palm tree tropical beach'],
      ['\u{1F335}','cactus desert plant'],
      ['\u{1F340}','four leaf clover luck lucky irish'],
      ['\u{2618}\u{FE0F}','shamrock irish clover'],
      ['\u{1F343}','leaf fluttering wind nature'],
      ['\u{1F342}','fallen leaf autumn fall'],
      ['\u{1F341}','maple leaf fall autumn canada'],
      ['\u{1F344}','mushroom fungi toadstool'],
      ['\u{1F436}','dog face puppy pet'],
      ['\u{1F431}','cat face kitten pet'],
      ['\u{1F42D}','mouse face rat'],
      ['\u{1F430}','rabbit face bunny'],
      ['\u{1F43B}','bear face teddy'],
      ['\u{1F98A}','fox face'],
      ['\u{1F981}','lion face king'],
      ['\u{1F42E}','cow face'],
      ['\u{1F437}','pig face'],
      ['\u{1F438}','frog face'],
      ['\u{1F435}','monkey face'],
      ['\u{1F427}','penguin bird'],
      ['\u{1F426}','bird tweet'],
      ['\u{1F985}','eagle bird'],
      ['\u{1F98B}','butterfly insect'],
      ['\u{1F41D}','bee honeybee buzz'],
      ['\u{1F422}','turtle slow tortoise'],
      ['\u{1F420}','tropical fish'],
      ['\u{1F42C}','dolphin ocean'],
      ['\u{1F433}','whale ocean sea'],
      ['\u{1F419}','octopus tentacles'],
    ],
  },
  {
    id: 'food', icon: '\u{1F354}', label: 'Food & Drink',
    items: [
      ['\u{1F34F}','green apple fruit'],
      ['\u{1F34E}','red apple fruit'],
      ['\u{1F350}','pear fruit'],
      ['\u{1F34A}','orange tangerine fruit'],
      ['\u{1F34B}','lemon citrus fruit'],
      ['\u{1F34C}','banana fruit'],
      ['\u{1F349}','watermelon fruit summer'],
      ['\u{1F347}','grapes fruit wine'],
      ['\u{1F353}','strawberry fruit berry'],
      ['\u{1F352}','cherries fruit'],
      ['\u{1F351}','peach fruit'],
      ['\u{1F96D}','mango fruit tropical'],
      ['\u{1F34D}','pineapple fruit tropical'],
      ['\u{1F965}','coconut tropical'],
      ['\u{1F95D}','kiwi fruit'],
      ['\u{1F345}','tomato vegetable'],
      ['\u{1F346}','eggplant aubergine'],
      ['\u{1F951}','avocado guacamole'],
      ['\u{1F966}','broccoli vegetable green'],
      ['\u{1F96C}','leafy green lettuce salad'],
      ['\u{1F952}','cucumber vegetable pickle'],
      ['\u{1F336}\u{FE0F}','hot pepper chili spicy'],
      ['\u{1F33D}','corn cob vegetable'],
      ['\u{1F955}','carrot vegetable orange'],
      ['\u{1F954}','potato vegetable'],
      ['\u{1F35E}','bread loaf'],
      ['\u{1F9C0}','cheese wedge'],
      ['\u{1F373}','cooking egg frying pan'],
      ['\u{1F953}','bacon meat breakfast'],
      ['\u{1F969}','steak meat beef cut'],
      ['\u{1F357}','poultry leg chicken drumstick'],
      ['\u{1F356}','meat bone'],
      ['\u{1F32D}','hot dog sausage'],
      ['\u{1F354}','hamburger burger cheeseburger'],
      ['\u{1F35F}','french fries chips'],
      ['\u{1F355}','pizza slice'],
      ['\u{1F96A}','sandwich sub'],
      ['\u{1F32E}','taco mexican'],
      ['\u{1F32F}','burrito wrap mexican'],
      ['\u{1F959}','stuffed flatbread pita wrap'],
      ['\u{1F957}','salad green healthy bowl'],
      ['\u{1F35D}','spaghetti pasta noodles italian'],
      ['\u{1F35C}','ramen noodles soup'],
      ['\u{1F372}','stew pot food'],
      ['\u{1F35B}','curry rice food'],
      ['\u{1F363}','sushi japanese fish rice'],
      ['\u{1F371}','bento box japanese lunch'],
      ['\u{1F364}','fried shrimp tempura'],
      ['\u{1F359}','rice ball onigiri japanese'],
      ['\u{1F370}','shortcake cake strawberry dessert'],
      ['\u{1F36D}','lollipop candy sweet'],
      ['\u{1F36C}','candy sweet'],
      ['\u{1F36B}','chocolate bar sweet'],
      ['\u{1F369}','doughnut donut sweet breakfast'],
      ['\u{1F36A}','cookie sweet biscuit'],
      ['\u{1F36F}','honey pot sweet bear'],
      ['\u{2615}','coffee hot beverage tea cup'],
      ['\u{1F375}','tea green cup drink'],
      ['\u{1F37C}','baby bottle milk'],
      ['\u{1F95B}','glass milk drink'],
      ['\u{1F9C3}','beverage box juice'],
      ['\u{1F964}','cup straw bubble tea drink'],
      ['\u{1F37A}','beer mug drink alcohol'],
      ['\u{1F37B}','clinking beer mugs cheers drink'],
      ['\u{1F942}','clinking glasses champagne cheers wine'],
      ['\u{1F377}','wine glass red drink alcohol'],
      ['\u{1F378}','cocktail martini drink'],
      ['\u{1F379}','tropical drink cocktail'],
      ['\u{1F9C9}','mate drink'],
    ],
  },
  {
    id: 'travel', icon: '\u{1F697}', label: 'Travel & Places',
    items: [
      ['\u{1F697}','car automobile red'],
      ['\u{1F695}','taxi cab yellow'],
      ['\u{1F699}','suv blue car'],
      ['\u{1F3CE}\u{FE0F}','racing car formula speed'],
      ['\u{1F68C}','bus public transit'],
      ['\u{1F691}','ambulance emergency medical'],
      ['\u{1F692}','fire engine truck emergency'],
      ['\u{1F693}','police car emergency'],
      ['\u{1F69A}','delivery truck moving'],
      ['\u{1F3CD}\u{FE0F}','motorcycle racing'],
      ['\u{1F6F5}','motor scooter moped'],
      ['\u{1F6B2}','bicycle bike cycling'],
      ['\u{1F6F4}','kick scooter'],
      ['\u{26FD}','fuel pump gas station'],
      ['\u{1F6A8}','police light siren emergency'],
      ['\u{1F6A5}','traffic light horizontal'],
      ['\u{1F6A6}','traffic light vertical'],
      ['\u{1F6D1}','stop sign octagonal'],
      ['\u{1F6A7}','construction barrier'],
      ['\u{2693}','anchor ship boat'],
      ['\u{26F5}','sailboat boat sailing'],
      ['\u{1F6A4}','speedboat motorboat'],
      ['\u{1F6F3}\u{FE0F}','passenger ship cruise'],
      ['\u{1F6A2}','ship boat'],
      ['\u{2708}\u{FE0F}','airplane plane travel fly'],
      ['\u{1F6EB}','airplane departure takeoff fly'],
      ['\u{1F6EC}','airplane arrival landing fly'],
      ['\u{1F4BA}','seat airplane chair'],
      ['\u{1F681}','helicopter fly'],
      ['\u{1F680}','rocket space launch'],
      ['\u{1F6F8}','ufo flying saucer alien space'],
      ['\u{1F3E0}','house home'],
      ['\u{1F3E1}','house garden home'],
      ['\u{1F3E2}','office building work'],
      ['\u{1F3E5}','hospital medical health'],
      ['\u{1F3E6}','bank money finance'],
      ['\u{1F3E8}','hotel travel stay'],
      ['\u{1F3EA}','convenience store shop'],
      ['\u{1F3EB}','school education learn'],
      ['\u{1F3ED}','factory industry'],
      ['\u{1F3F0}','castle european kingdom'],
      ['\u{1F5FC}','tokyo tower japan'],
      ['\u{1F5FD}','statue liberty new york usa'],
      ['\u{26EA}','church religion'],
      ['\u{1F54C}','mosque religion islam'],
      ['\u{1F54D}','synagogue religion jewish'],
      ['\u{26F2}','fountain water park'],
      ['\u{26F0}\u{FE0F}','mountain nature hiking'],
      ['\u{1F3D6}\u{FE0F}','beach island umbrella vacation'],
      ['\u{1F3DD}\u{FE0F}','desert island palm tree'],
      ['\u{1F305}','sunrise morning'],
      ['\u{1F307}','sunset city evening'],
      ['\u{1F303}','night stars city'],
      ['\u{1F309}','bridge night city'],
      ['\u{1F30D}','globe earth world europe africa'],
      ['\u{1F30E}','globe earth world americas'],
      ['\u{1F30F}','globe earth world asia australia'],
    ],
  },
  {
    id: 'objects', icon: '\u{1F4A1}', label: 'Objects & Tech',
    items: [
      ['\u{231A}','watch time wrist clock'],
      ['\u{1F4F1}','mobile phone cell smartphone'],
      ['\u{1F4BB}','laptop computer work'],
      ['\u{2328}\u{FE0F}','keyboard type computer'],
      ['\u{1F5A5}\u{FE0F}','desktop computer monitor screen'],
      ['\u{1F5A8}\u{FE0F}','printer print'],
      ['\u{1F4F7}','camera photo picture'],
      ['\u{1F4F9}','video camera record'],
      ['\u{1F3A5}','movie camera film cinema'],
      ['\u{1F4FA}','television tv screen'],
      ['\u{1F4FB}','radio'],
      ['\u{1F399}\u{FE0F}','studio microphone podcast record'],
      ['\u{23F0}','alarm clock wake time'],
      ['\u{231B}','hourglass time sand'],
      ['\u{1F4E1}','satellite antenna signal dish'],
      ['\u{1F50B}','battery power charge'],
      ['\u{1F50C}','electric plug power charge'],
      ['\u{1F4A1}','light bulb idea bright'],
      ['\u{1F526}','flashlight torch light'],
      ['\u{1F56F}\u{FE0F}','candle light flame'],
      ['\u{1F4B0}','money bag dollar rich'],
      ['\u{1F4B5}','dollar bill money cash'],
      ['\u{1F4B3}','credit card payment bank'],
      ['\u{1F9FE}','receipt bill payment'],
      ['\u{2709}\u{FE0F}','envelope email mail letter'],
      ['\u{1F4E7}','email message at'],
      ['\u{1F4E8}','incoming envelope email'],
      ['\u{1F4E9}','envelope arrow email send'],
      ['\u{1F4E6}','package box delivery shipping'],
      ['\u{1F4EC}','mailbox open letters'],
      ['\u{1F4DD}','memo note writing pencil'],
      ['\u{1F4C1}','file folder directory'],
      ['\u{1F4C2}','open file folder directory'],
      ['\u{1F4C5}','calendar date day schedule'],
      ['\u{1F4C6}','tear off calendar date'],
      ['\u{1F4C8}','chart increasing graph up trend'],
      ['\u{1F4C9}','chart decreasing graph down trend'],
      ['\u{1F4CA}','bar chart statistics graph'],
      ['\u{1F4CB}','clipboard list notes'],
      ['\u{1F4CC}','pushpin pin location mark'],
      ['\u{1F4CE}','paperclip attachment attach'],
      ['\u{2702}\u{FE0F}','scissors cut'],
      ['\u{1F512}','lock locked secure padlock'],
      ['\u{1F513}','unlock unlocked open padlock'],
      ['\u{1F511}','key access password unlock'],
      ['\u{1F50D}','magnifying glass search zoom left'],
      ['\u{1F50E}','magnifying glass search zoom right'],
      ['\u{1F6CE}\u{FE0F}','bellhop bell notification'],
      ['\u{1F514}','bell notification alert ring'],
      ['\u{1F515}','bell slash mute silent'],
      ['\u{1F4E3}','megaphone announcement cheering'],
      ['\u{1F4E2}','loudspeaker announcement public'],
      ['\u{1F508}','speaker low volume sound'],
      ['\u{1F509}','speaker medium volume sound'],
      ['\u{1F50A}','speaker high volume sound loud'],
      ['\u{1F507}','speaker muted mute silent off'],
      ['\u{1F4F2}','mobile phone arrow call'],
      ['\u{260E}\u{FE0F}','telephone phone call'],
      ['\u{1F4DE}','telephone receiver phone call'],
      ['\u{1F4DF}','pager beeper'],
      ['\u{1F4E0}','fax machine'],
      ['\u{1F52D}','telescope space astronomy science'],
    ],
  },
  {
    id: 'medical', icon: '\u{1FA7A}', label: 'Medical & Health',
    items: [
      ['\u{1FA7A}','stethoscope doctor medical health'],
      ['\u{1F489}','syringe needle vaccine injection shot'],
      ['\u{1FA78}','drop of blood donate transfusion'],
      ['\u{1F48A}','pill medicine capsule drug pharmacy prescription'],
      ['\u{1FA79}','adhesive bandage band aid wound'],
      ['\u{1F321}\u{FE0F}','thermometer temperature fever check'],
      ['\u{1F9F4}','lotion bottle skincare dermatology'],
      ['\u{1F9FC}','soap hygiene hand washing sanitize clean'],
      ['\u{1F9EC}','dna genetics double helix genomics'],
      ['\u{1F9EA}','test tube lab science chemistry specimen'],
      ['\u{1F9EB}','petri dish lab bacteria culture science'],
      ['\u{1F9A0}','microbe germ bacteria virus pathogen infection'],
      ['\u{1F52C}','microscope science lab research pathology'],
      ['\u{2695}\u{FE0F}','medical symbol caduceus health'],
      ['\u{2622}\u{FE0F}','radioactive radiation oncology radiology'],
      ['\u{2623}\u{FE0F}','biohazard infectious hazardous contamination'],
      ['\u{1F9D1}\u{200D}\u{2695}\u{FE0F}','health worker doctor nurse practitioner'],
      ['\u{1F468}\u{200D}\u{2695}\u{FE0F}','man doctor male physician surgeon'],
      ['\u{1F469}\u{200D}\u{2695}\u{FE0F}','woman doctor female physician surgeon nurse'],
      ['\u{1F3E5}','hospital building medical health clinic'],
      ['\u{1F691}','ambulance emergency medical ems'],
      ['\u{1F6CC}','person in bed patient rest hospital recovery sleep'],
      ['\u{1F4A4}','zzz sleep rest sedation anesthesia'],
      ['\u{267F}','wheelchair accessible disability'],
      ['\u{1F9BD}','manual wheelchair mobility'],
      ['\u{1F9BC}','motorized wheelchair electric mobility'],
      ['\u{1F9AF}','white cane probing blind accessibility'],
      ['\u{1F9AE}','guide dog service animal blind accessibility'],
      ['\u{1F9D1}\u{200D}\u{1F9AF}','person with white cane blind visually impaired'],
      ['\u{1F6B6}','person walking pedestrian exercise physical therapy'],
      ['\u{1F3C3}','person running jogging exercise cardio'],
      ['\u{1F9D8}','person lotus yoga meditation zen wellness'],
      ['\u{1F930}','pregnant woman expecting obstetrics maternity'],
      ['\u{1F931}','breast feeding nursing baby maternity lactation'],
      ['\u{1F637}','face mask sick medical protective'],
      ['\u{1F912}','thermometer face sick fever ill temperature'],
      ['\u{1F915}','head bandage hurt injury concussion wound'],
      ['\u{1F922}','nauseous face sick green queasy'],
      ['\u{1F92E}','vomiting face sick nausea'],
      ['\u{1F927}','sneezing face tissue cold flu allergy'],
      ['\u{1F975}','hot face fever overheated heatstroke'],
      ['\u{1F976}','cold face freezing hypothermia'],
      ['\u{1F9B7}','tooth dental dentist teeth oral'],
      ['\u{1F9B4}','bone skeleton x-ray orthopedic fracture'],
      ['\u{1F9E0}','brain neurology mind mental cognitive'],
      ['\u{1F441}\u{FE0F}','eye vision ophthalmology optical'],
      ['\u{1F442}','ear hearing audiology ent'],
      ['\u{1F443}','nose smell ent sinus nasal'],
      ['\u{1F9B5}','leg orthopedic knee physical therapy'],
      ['\u{1F9B6}','foot podiatry ankle toe'],
      ['\u{1F9BE}','mechanical arm prosthetic artificial limb'],
      ['\u{1F9BF}','mechanical leg prosthetic artificial limb'],
      ['\u{2764}\u{FE0F}\u{200D}\u{1FA79}','mending heart recovery healing rehab'],
    ],
  },
  {
    id: 'symbols', icon: '\u{1F523}', label: 'Symbols & Flags',
    items: [
      ['\u{1F4AF}','hundred percent score perfect'],
      ['\u{1F522}','input numbers 1234'],
      ['\u{2705}','check mark green done complete yes'],
      ['\u{274C}','cross mark red no wrong delete'],
      ['\u{274E}','cross mark square'],
      ['\u{2714}\u{FE0F}','check mark heavy done'],
      ['\u{2716}\u{FE0F}','multiplication sign cross heavy'],
      ['\u{2795}','plus add'],
      ['\u{2796}','minus subtract'],
      ['\u{2797}','divide division'],
      ['\u{27B0}','curly loop'],
      ['\u{267E}\u{FE0F}','infinity forever loop'],
      ['\u{2753}','question mark red'],
      ['\u{2754}','question mark white'],
      ['\u{2755}','exclamation mark white'],
      ['\u{2757}','exclamation mark red'],
      ['\u{203C}\u{FE0F}','double exclamation bang'],
      ['\u{2049}\u{FE0F}','exclamation question interrobang'],
      ['\u{1F198}','sos help emergency'],
      ['\u{1F199}','up arrow squared'],
      ['\u{1F192}','cool squared word'],
      ['\u{1F193}','free squared word'],
      ['\u{1F195}','new squared word'],
      ['\u{1F197}','ok squared word'],
      ['\u{1F6AB}','prohibited no forbidden ban'],
      ['\u{1F4F5}','no mobile phones'],
      ['\u{1F6B7}','no pedestrians'],
      ['\u{26A0}\u{FE0F}','warning sign alert caution'],
      ['\u{1F6B8}','children crossing school'],
      ['\u{267B}\u{FE0F}','recycling symbol recycle green'],
      ['\u{1F530}','japanese symbol beginner new'],
      ['\u{25B6}\u{FE0F}','play button'],
      ['\u{23F8}\u{FE0F}','pause button'],
      ['\u{23F9}\u{FE0F}','stop button'],
      ['\u{23FA}\u{FE0F}','record button red'],
      ['\u{23ED}\u{FE0F}','next track button'],
      ['\u{23EE}\u{FE0F}','last track button'],
      ['\u{1F500}','shuffle random'],
      ['\u{1F501}','repeat loop'],
      ['\u{1F504}','arrows counterclockwise refresh reload'],
      ['\u{25AA}\u{FE0F}','black small square'],
      ['\u{25AB}\u{FE0F}','white small square'],
      ['\u{1F536}','large orange diamond'],
      ['\u{1F537}','large blue diamond'],
      ['\u{1F538}','small orange diamond'],
      ['\u{1F539}','small blue diamond'],
      ['\u{1F3C1}','checkered flag finish race'],
      ['\u{1F6A9}','triangular flag'],
      ['\u{1F3F4}','black flag'],
      ['\u{1F3F3}\u{FE0F}','white flag surrender'],
      ['\u{1F3F3}\u{FE0F}\u{200D}\u{1F308}','rainbow flag pride lgbtq'],
      ['\u{1F1FA}\u{1F1F8}','flag us usa america united states'],
      ['\u{1F1EC}\u{1F1E7}','flag gb uk britain england'],
      ['\u{1F1E8}\u{1F1E6}','flag ca canada'],
      ['\u{1F1E6}\u{1F1FA}','flag au australia'],
      ['\u{1F1E9}\u{1F1EA}','flag de germany'],
      ['\u{1F1EB}\u{1F1F7}','flag fr france'],
      ['\u{1F1EA}\u{1F1F8}','flag es spain'],
      ['\u{1F1EE}\u{1F1F9}','flag it italy'],
      ['\u{1F1EF}\u{1F1F5}','flag jp japan'],
      ['\u{1F1F0}\u{1F1F7}','flag kr korea south'],
      ['\u{1F1E7}\u{1F1F7}','flag br brazil'],
      ['\u{1F1F2}\u{1F1FD}','flag mx mexico'],
      ['\u{1F1EE}\u{1F1F3}','flag in india'],
      ['\u{1F1E8}\u{1F1F3}','flag cn china'],
      ['\u{1F1F5}\u{1F1F7}','flag pr puerto rico'],
    ],
  },
];
